<div class="flex h-full w-full flex-col">
  @if (deleteEntitiesModal().isOpen) {
    <app-modal
      [config]="deleteEntitiesModal().config"
      (closeModal)="deleteEntitiesModal().isOpen = false"
      [title]="deleteEntitiesModal().title()">
      <div modal-body>
        {{
          (getPageContext() === 'data'
            ? 'features.entities.delete_modal.description'
            : 'features.plan.remove_data.description'
          ) | translate: { count: getAllSelectedEntityUids(), entityType: entityName() }
        }}
      </div>
    </app-modal>
  }

  @if (addToPlanModal().isOpen) {
    <app-modal
      [config]="addToPlanModal().config"
      (closeModal)="addToPlanModal().isOpen = false"
      [title]="addToPlanModal().title()">
      <div modal-body>
        {{
          'features.plan.add_data.description'
            | translate: { count: getAllSelectedEntityUids(), entityType: entityName() }
        }}
      </div>
    </app-modal>
  }

  <app-toolbox
    (addToPlan)="onAddToPlan()"
    (deselectAll)="onDeselectAll()"
    (loadAllRows)="loadAllRows()"
    (selectOnly)="onSelectedOnlyChange($event)"
    (pageChange)="onPageChange($event)"
    (advancedQuerySearch)="onAdvancedQuerySearch($event)"
    [useAdvancedFiltering]="true"
    [(showAdvancedFiltering)]="showAdvancedFiltering"
    [openFilterForField]="openFilterForField()"
    [paginationConfig]="paginationConfig()"
    [sections]="previewOnly() ? [] : toolboxSections()"
    [selectedItemsCount]="nbSelectedItems()"
    [selectionVariant]="'data'"
    [showPageSelector]="true"
    [showPlanItems]="showPlanToolbox() && !previewOnly()"
    [searchConfig]="toolboxSearchConfig()"
    [showCustomContent]="activeGrammar() === 'path'"
    [shouldFocus]="shouldFocus()"
    [showSelect]="showSelect()"
    [displayedItemCount]="entitiesArrayInPage().length"
    [totalItems]="paginationState().totalItems"
    [searchPlaceholder]="searchPlaceholder()"
    [searchQuery]="searchQuery()">
    @if (activeGrammar() === 'path' && vocabulary(); as vocab) {
      <app-path-filter-search-bar
        class="min-w-80 flex-1"
        [vocabulary]="vocab"
        [state]="pathBar.bar()"
        [shortcut]="ownsSearchShortcut()"
        [extraSortOptions]="sortTabOptions()"
        (stateChange)="pathBar.editBar($event)" />
    }
  </app-toolbox>

  <app-plan-edit-banner
    class="block px-3 py-2 empty:hidden"
    [planId]="planIdInView()"
    [flavour]="entityVersionName() ?? null" />
  <app-plan-edit-bar class="block px-3 py-2 empty:hidden" [planId]="planIdInView()" />

  @if (selectionBanner(); as banner) {
    <div
      class="bg-accent-interactive text-accent-main border-accent-border flex flex-wrap items-center gap-2 border-b px-3 py-1.5 text-xs">
      @if (banner.kind === 'page-selected') {
        <span>{{
          'features.data.banner.page_selected' | translate: { count: banner.pageCount }
        }}</span>
        <button
          type="button"
          class="font-semibold underline disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="pathBar.holed()"
          [title]="
            pathBar.holed()
              ? ('features.data.filter_presets.template.select_all_blocked' | translate)
              : ''
          "
          (click)="onSelectAll()">
          {{
            'features.data.banner.select_all_matching' | translate: { total: banner.total }
          }}
        </button>
        @if (pathBar.holed()) {
          <span class="text-text-tertiary">{{
            'features.data.filter_presets.template.select_all_blocked' | translate
          }}</span>
        }
      } @else {
        <span>{{
          'features.data.banner.all_selected' | translate: { total: banner.total }
        }}</span>
        @if (banner.overCap) {
          <span class="text-text-tertiary">{{
            'features.data.banner.over_cap_note'
              | translate: { limit: selectAllSummaryMaxRows }
          }}</span>
        }
        <button type="button" class="font-semibold underline" (click)="onDeselectAll()">
          {{ 'features.data.banner.clear' | translate }}
        </button>
      }
    </div>
  }

  @if (activeGrammar() === 'path' && showAdvancedFiltering() && vocabulary(); as vocab) {
    <app-path-filter-pills
      class="border-weak bg-elevated block border-b"
      [vocabulary]="vocab"
      [draft]="pathBar.draft()"
      [invalidPaths]="pathBar.invalidPaths()"
      (draftChange)="pathBar.editDraft($event)"
      (openAdvanced)="pathBar.pathPanel.set('filter')">
      <button
        sort-chip
        type="button"
        class="hover:bg-interactive hover:text-text-primary inline-flex cursor-pointer items-center gap-1.5 rounded-xs px-1.5 py-[3px] text-xs transition-colors"
        [class.text-text-secondary]="pathBar.sort().length === 0"
        [class.text-accent-main]="pathBar.sort().length > 0"
        (click)="pathBar.pathPanel.set('sort')">
        <i [class]="ICONS.sortAscending" aria-hidden="true"></i
        >{{ 'features.data.path_filter.sort.button' | translate }}
        @if (pathBar.sort().length > 0) {
          · {{ pathBar.sort().length }}
        }
      </button>
      @if (!previewOnly()) {
        <app-filter-presets filter-presets />
      }
    </app-path-filter-pills>
  } @else if (showAdvancedFiltering()) {
    <app-filter-query-builder
      [availableFields]="availableAdvancedFilters()"
      [initialConfiguration]="advancedFilters()"
      [openFilterForField]="openFilterForField()"
      (filterChange)="onFilterChanges($event)">
      @if (!previewOnly()) {
        <app-filter-presets filter-presets />
      }
    </app-filter-query-builder>
  }

  <div class="flex min-h-0 w-full flex-1">
    @if (activeGrammar() === 'path' && pathBar.pathPanel() && vocabulary(); as vocab) {
      <app-path-filter-panel
        appFocusFirst
        appReturnFocus
        class="h-full max-w-[60%] shrink-0"
        width="640px"
        dock="left"
        [(tab)]="pathBar.pathPanel"
        [title]="'features.data.path_filter.panel.title' | translate"
        [entityType]="entityType().toUpperCase()"
        [count]="paginationState().totalItems"
        [error]="pathBar.error()"
        (closed)="pathBar.pathPanel.set(null)">
        <app-path-filter-sort-tab
          panel-sort
          [options]="sortTabOptions()"
          [sort]="pathBar.sort()"
          (sortChange)="pathBar.editSort($event)" />
        <span panel-sort-footer>{{
          'features.data.path_filter.sort.footer' | translate
        }}</span>
        <app-path-filter-composer
          panel-body
          [vocabulary]="vocab"
          [draft]="pathBar.draft()"
          [mode]="composerMode()"
          [parameters]="composerParameters()"
          [invalidPaths]="pathBar.invalidPaths()"
          (draftChange)="pathBar.editDraft($event)"
          (newParameterRequested)="onNewParameterRequested($event)" />
        @if (!previewOnly()) {
          <app-filter-template-author
            panel-body
            [vocabulary]="vocab"
            [draft]="pathBar.draft()"
            (draftChange)="pathBar.editDraft($event)" />
        }
        <div panel-footer class="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
          <i [class]="ICONS.checkCircle"></i>
          <span>{{ 'features.data.path_filter.panel.applied' | translate }}</span>
          <span class="flex-1"></span>
          @if (!previewOnly() && !filterPresetsStore.templateAuthoring()) {
            <button
              type="button"
              class="btn-cancel btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
              (click)="filterPresetsStore.templateAuthoring.set(true)">
              <i [class]="ICONS.bracketsCurly"></i>
              {{ 'features.data.filter_presets.template.save_as' | translate }}
            </button>
          }
          <app-path-filter-move-buttons />
          <button type="button" class="btn-cancel btn-sm" (click)="pathBar.clearByHand()">
            {{ 'common.clear' | translate }}
          </button>
        </div>
      </app-path-filter-panel>
    }
    <div class="flex min-h-0 min-w-0 flex-1 flex-col">
      @if (isUploadZoneOpen() && canUpload()) {
        <app-upload-zone
          class="h-full min-h-0 w-full overflow-hidden rounded-sm"
          [config]="uploadZoneConfig()"
          (uploadClicked)="onFilesDropped($event)">
        </app-upload-zone>
      } @else {
        <as-split direction="vertical" unit="percent" class="h-full w-full">
          <as-split-area [visible]="'true'">
            <div
              #detailsHost
              class="flex h-full w-full flex-col overflow-hidden rounded-sm">
              <as-split direction="vertical" unit="pixel" class="h-full w-full">
                <as-split-area size="*" [visible]="'true'">
                  <app-table
                    [data]="tableEntities()"
                    [allColumns]="columns()"
                    [actions]="previewOnly() ? [] : actions()"
                    [selected]="selectedItemsSet()"
                    [ownerEntityType]="entityType()"
                    [tableId]="entityType()"
                    [config]="tableConfig()"
                    [summaryItems]="$any(summaryItems())"
                    [referenceNames]="referenceNameLookup"
                    [pendingRowUids]="pendingRouteUids()"
                    [initialSort]="presetSort()"
                    [clientSort]="activeGrammar() !== 'path'"
                    (addFilterOn)="addFilterOn($event)"
                    (removeFilterOn)="removeFilterOn($event)"
                    (sortChange)="onSortChange($event)"
                    (rowEdit)="onRowEdit($event)"
                    (showDetails)="onShowDetails($event)"
                    (listItemClick)="onListItemClick($event)" />
                </as-split-area>
                @if (detailPanes().length > 0) {
                  <as-split-area [size]="detailsPaneHeight()" [visible]="'true'">
                    <as-split direction="horizontal" unit="percent" class="h-full w-full">
                      @for (pane of detailPanes(); track $index; let groupIndex = $index) {
                        <as-split-area [visible]="'true'">
                          <div class="bg-elevated flex h-full min-h-0 flex-col">
                            <app-entity-detail-tabs
                              [uids]="pane.uids"
                              [activeUid]="pane.activeUid"
                              [splitTarget]="pane.splitTarget"
                              [listId]="detailListIds()[groupIndex]"
                              [connectedTo]="detailListIds()"
                              (activate)="onActivateDetail($event)"
                              (closeOne)="onRemoveDetails($event)"
                              (closeAll)="onCloseAllDetails(groupIndex)"
                              (split)="onSplitDetail($event)"
                              (dropped)="onDropDetail($event, groupIndex)" />
                            @if (pane.active; as detail) {
                              <div class="min-h-0 flex-1">
                                <app-entity-details [entityInput]="detail" />
                              </div>
                            }
                          </div>
                        </as-split-area>
                      }
                    </as-split>
                  </as-split-area>
                }
              </as-split>
            </div>
          </as-split-area>
          @if (isVisuAllowed()) {
            <as-split-area [visible]="!isPopupOpen() && isVisuEnabled()">
              <app-data-visu
                class="w-full overflow-hidden"
                [filteringEnabled]="true"
                [inPlaceEditingEnabled]="true" />
            </as-split-area>
          }
        </as-split>
      }
    </div>
  </div>

  <app-reference-popup-outlet
    [isColumnFilterable]="isColumnFilterable"
    [includeInFilterStatus]="includeInFilterStatusFor"
    (addToFilter)="onListItemClick($any($event))"
    (includeInFilter)="onIncludeInFilter($event)" />
</div>
